import { pricingPlans } from "@/mocks/pricingPlans";
import type { SchoolPlan } from "@/types";
import Reveal from "@/components/site/Reveal";
import SectionTitle from "@/components/site/SectionTitle";
import SiteButton from "@/components/site/SiteButton";

interface PricingSectionProps {
  onRequest: (plan: SchoolPlan) => void;
}

export default function PricingSection({ onRequest }: PricingSectionProps) {
  return (
    <section id="tarifs" className="scroll-mt-28 bg-background-100 py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <SectionTitle
          eyebrow="Tarifs"
          title="Un prix à l'année, sans surprise"
          intro="Montants en francs guinéens, par école et par année. Le forfait est confirmé avec vous à l'ouverture du compte."
        />

        <div className="mt-14 grid gap-5 lg:grid-cols-3 lg:items-stretch">
          {pricingPlans.map((plan, index) => {
            const dark = plan.popular;
            return (
              <Reveal key={plan.id} delay={index * 90} className="h-full">
                <div
                  className={`flex h-full flex-col rounded-[1.75rem] border p-7 ${
                    dark ? "border-primary-950 bg-primary-950 text-white shadow-modal" : "border-background-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className={`text-xl font-bold font-heading ${dark ? "text-white" : "text-foreground-950"}`}>{plan.name}</h3>
                    {dark && (
                      <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white">
                        Tout inclus
                      </span>
                    )}
                  </div>
                  <p className={`mt-1 text-sm ${dark ? "text-white/75" : "text-foreground-600"}`}>{plan.tagline}</p>

                  <p className="mt-6 flex items-baseline gap-2">
                    <span className={`text-4xl font-extrabold font-heading tabular-nums ${dark ? "text-white" : "text-primary-950"}`}>
                      {plan.price > 0 ? plan.price.toLocaleString("fr-FR") : "Sur devis"}
                    </span>
                    {plan.price > 0 && (
                      <span className={`text-sm ${dark ? "text-white/70" : "text-foreground-500"}`}>{plan.priceSuffix}</span>
                    )}
                  </p>

                  <ul className={`mt-6 flex-1 space-y-3 border-t pt-6 ${dark ? "border-white/15" : "border-background-200"}`}>
                    {plan.features.map((feature) => (
                      <li key={feature} className={`flex gap-2.5 text-[0.95rem] leading-snug ${dark ? "text-white/90" : "text-foreground-700"}`}>
                        <i className={`ri-check-line mt-0.5 shrink-0 ${dark ? "text-primary-300" : "text-primary-600"}`} aria-hidden="true"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <SiteButton
                    variant={dark ? "light" : "outline"}
                    className="mt-8 w-full"
                    onClick={() => onRequest(plan.plan)}
                  >
                    Choisir {plan.name}
                  </SiteButton>
                  <p className={`mt-3 text-xs leading-relaxed ${dark ? "text-white/60" : "text-foreground-500"}`}>{plan.note}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
